/**
 * UserPlaylists Component
 *
 * Display public playlists from a selected member
 */

import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import './UserPlaylists.css';

function UserPlaylists({ member, onBack, currentUser, onLoadPlaylist }) {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPlaylists = async () => {
      if (!member) return;

      try {
        setLoading(true);

        // Query for public playlists from this user
        // Note: Using only where clauses (no orderBy) to avoid requiring a composite index
        // We'll sort in JavaScript instead
        const playlistsQuery = query(
          collection(db, 'playlists'),
          where('userId', '==', member.id),
          where('isPublic', '==', true)
        );

        const playlistsSnapshot = await getDocs(playlistsQuery);
        const playlistsData = playlistsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort by updatedAt in JavaScript (descending - most recent first)
        playlistsData.sort((a, b) => {
          const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return bTime - aTime; // Descending order
        });

        setPlaylists(playlistsData);
      } catch (err) {
        console.error('Error loading playlists:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPlaylists();
  }, [member]);

  if (loading) {
    return (
      <div className="user-playlists-page">
        <div className="user-playlists-header">
          <button className="back-button" onClick={onBack}>
            ← Back to Members
          </button>
          <h1>{member.displayName}'s Playlists</h1>
        </div>
        <div className="user-playlists-loading">
          <div className="loading-spinner"></div>
          <p>Loading playlists...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-playlists-page">
        <div className="user-playlists-header">
          <button className="back-button" onClick={onBack}>
            ← Back to Members
          </button>
          <h1>{member.displayName}'s Playlists</h1>
        </div>
        <div className="user-playlists-error">
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-playlists-page">
      <div className="user-playlists-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Members
        </button>
        <div className="user-info">
          <div className="user-avatar">
            {member.photoURL ? (
              <img src={member.photoURL} alt={member.displayName} />
            ) : (
              <div className="user-avatar-placeholder">
                {member.displayName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div>
            <h1>{member.displayName}'s Playlists</h1>
            <p className="user-email">{member.email}</p>
          </div>
        </div>
      </div>

      {playlists.length === 0 ? (
        <div className="user-playlists-empty">
          <div className="empty-icon">📋</div>
          <h2>No public playlists</h2>
          <p>This member hasn't shared any public playlists yet.</p>
        </div>
      ) : (
        <div className="user-playlists-list">
          {playlists.map((playlist) => (
            <div key={playlist.id} className="user-playlist-card">
              <div className="user-playlist-info">
                <h3 className="user-playlist-name">{playlist.name}</h3>
                {playlist.description && (
                  <p className="user-playlist-description">
                    {playlist.description}
                  </p>
                )}
                <div className="user-playlist-meta">
                  <span className="user-playlist-tracks">
                    🎵 {playlist.trackCount} song
                    {playlist.trackCount !== 1 ? 's' : ''}
                  </span>
                  <span className="user-playlist-date">
                    {new Date(
                      playlist.updatedAt || playlist.createdAt
                    ).toLocaleDateString()}
                  </span>
                </div>
              </div>
              {onLoadPlaylist && (
                <div className="user-playlist-actions">
                  <button
                    className="btn-load-playlist"
                    onClick={() => onLoadPlaylist(playlist)}
                  >
                    ▶ Play
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default UserPlaylists;
